import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  TextField,
  Chip,
  Card,
  CardContent,
  IconButton,
  Avatar,
} from '@mui/material';

// Page Layout
export const PageContainer = styled('div')({
  minHeight: '100vh',
  backgroundColor: '#f6f6f8',
  position: 'relative',
  marginBottom: '124px',
});

export const MainContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
});

// Header Section
export const HeaderSection = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const HeaderInfoContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const HeaderTextContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  textAlign: 'right',
});

export const BackButton = styled(Button)({
  backgroundColor: '#5b3fff',
  color: 'white',
  padding: '12px 24px',
  borderRadius: 8,
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  '&:hover': {
    backgroundColor: '#4a2fe8',
  },
});

// Channel Card
export const ChannelCard = styled(Card)({
  border: '1px solid #e6e6e6',
  borderRadius: 16,
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
  marginBottom: '24px',
});

export const ChannelCardHeader = styled('div')({
  display: 'flex',
  padding: '16px 24px',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: '#ffffff',
  borderBottom: '1px solid #f0f0f0',
  '&:hover': {
    backgroundColor: '#fafafa',
  },
});

export const ChannelInfo = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
});

export const ChannelIcon = styled(Avatar)({
  width: 32,
  height: 32,
  borderRadius: '50%',
});

export const StatusChip = styled(Chip)({
  backgroundColor: '#e7f6ec',
  color: '#1a8a4a',
  height: '24px',
  borderRadius: '9999px',
  '& .MuiChip-label': {
    padding: '2px 8px',
    fontSize: '12px',
    fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  },
  '& .MuiChip-icon': {
    fontSize: '14px',
    marginLeft: '6px',
    marginRight: '-4px',
  },
});

export const ExpandButton = styled(IconButton)({
  color: '#666',
});

export const ChannelCardContent = styled(CardContent)({
  padding: '0',
});

// Filter Section
export const FilterContainer = styled('div')({
  display: 'flex',
  padding: '16px 24px 24px 24px',
  gap: '16px',
});

export const SearchContainer = styled(TextField)({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    height: '40px',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    fontSize: '14px',
    color: 'rgba(10, 10, 10, 0.5)',
    padding: '8px 16px',
  },
});

// Units List
export const UnitsList = styled(Box)({
});

export const UnitRow = styled(Box)<{ $isSelected: boolean; $isMapped: boolean }>(({ $isSelected, $isMapped }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 16px',
  minHeight: '50px',
  borderBottom: '1px solid #e9e9e7',
  backgroundColor: $isSelected ? '#eeebff' : $isMapped ? '#fafafa' : '#ffffff',
  opacity: $isMapped ? 0.5 : 1,
  cursor: $isMapped ? 'not-allowed' : 'pointer',
  transition: 'all 0.2s ease',
  direction: 'rtl',
  '&:hover': {
    backgroundColor: $isMapped ? '#fafafa' : $isSelected ? '#eeebff' : '#fafafa',
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const UnitImage = styled(Avatar)({
  width: '63px',
  height: '30px',
  borderRadius: '2px',
});

export const UnitInfoCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  justifyContent: 'flex-start',
});

export const UnitInfo = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const UnitName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#331860',
  fontFamily: '"Tajawal", "IBM Plex Sans Arabic", sans-serif',
  textAlign: 'right',
});

export const UnitId = styled(Typography)({
  fontSize: '12px',
  color: '#646e82',
  fontFamily: '"Tajawal", "IBM Plex Sans Arabic", sans-serif',
  textAlign: 'right',
});

// Status Badge
export const StatusBadge = styled(Box)<{ $status: 'synced' | 'not_synced' | 'failed' }>(({ $status }) => ({
  padding: '2px 8px',
  borderRadius: '9999px',
  fontSize: '12px',
  fontWeight: 400,
  fontFamily: '"IBM Plex Sans Arabic", sans-serif',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  lineHeight: '16px',
  backgroundColor: $status === 'synced' ? '#e7f6ec' : '#f3f4f6',
  color: $status === 'synced' ? '#1a8a4a' : '#6b7280',
}));

export const StatusDot = styled('span')<{ $status: 'synced' | 'not_synced' | 'failed' }>(({ $status }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: $status === 'synced' ? '#1a8a4a' : '#6b7280',
}));

// Table
export const TableHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 16px',
  borderBottom: '1px solid #e9e9e7',
  backgroundColor: '#fafafa',
  direction: 'rtl',
});

export const TableHeaderCell = styled(Typography)({
  fontSize: '12px',
  fontWeight: 700,
  color: '#37352f',
  fontFamily: '"Tajawal", "IBM Plex Sans Arabic", sans-serif',
  textAlign: 'right',
});
